import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAppStatusActions } from '@/db/useAppStatusActions';
import { appStatusDraftStatusState } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import type { AppStatusDocKind } from '@rock/types';
import type { FC } from 'react';

type AppStatusStagingToolbarProps = {
    kind: AppStatusDocKind;
    /**
     * When set (non-empty), Publish opens a confirmation dialog with this message before promoting
     * the draft. Pages pass a message for dangerous publishes (enabling maintenance, raising the
     * required version) and undefined for routine ones.
     */
    confirmMessage?: string;
};

/**
 * Draft controls for one app-status tool. Appears only when there's something to publish (a draft
 * that differs from live, or a brand-new one); publishing this tool never touches another tool's
 * pending draft. Mirrors EquipmentTypeStagingToolbar.
 */
export const AppStatusStagingToolbar: FC<AppStatusStagingToolbarProps> = ({ kind, confirmMessage }) => {
    const status = useAsyncAtomValue(appStatusDraftStatusState(kind), undefined);
    const { busy, publish, discardDraft } = useAppStatusActions(kind);

    if (status !== 'draft-differs' && status !== 'draft-pending') return null;

    return (
        <div className="flex flex-row items-center gap-2 rounded-md border bg-muted/40 px-3 py-2">
            <Badge variant="outline" className="text-blue-700 border-blue-700 bg-background">
                {status === 'draft-pending' ? 'Unpublished (new)' : 'Unpublished changes'}
            </Badge>
            <div className="flex-1" />
            {confirmMessage ? (
                <AlertDialog>
                    <AlertDialogTrigger asChild>
                        <Button size="sm" disabled={busy}>
                            Publish
                        </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Publish to every user?</AlertDialogTitle>
                            <AlertDialogDescription>{confirmMessage}</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={publish}>Publish</AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            ) : (
                <Button size="sm" disabled={busy} onClick={publish}>
                    Publish
                </Button>
            )}
            <Button size="sm" variant="ghost" disabled={busy} onClick={discardDraft}>
                Discard
            </Button>
        </div>
    );
};
